import { FC, useCallback, useMemo } from 'react';

import { intersection, isEmpty } from 'lodash/fp';

import { Button, FlexRow, Tooltip } from '@portal/common/components';
import { METRIC_ID_KEY } from '@portal/common/models/data-key';
import { styled } from '@portal/common/theme';
import { DataFilters, DataGranularity, DataType } from '@portal/common/types';
import { formatValue } from '@portal/common/utility/filters-helpers';

import _ from '../../../locale';
import { pluralizeVerb } from '../../../utility/string-utils';
import { useDataExplorerLayout } from '../context';

type Props = {
    setModalVisibility: (value: boolean) => void;
    selectedConditionsDataTypes: DataType[];
    selectedRefinementFilters: DataFilters;
    supportedDataTypes: DataType[];
    disabled: boolean;
    selectedFilters: DataFilters;
    granularity: DataGranularity | null;
};

const StyledRow = styled(FlexRow)<{ isCompact?: boolean }>(({ isCompact }) => ({
    marginTop: isCompact ? -12 : -30,
    marginBottom: isCompact ? 0 : 15,
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: '0 0 0 12px',
    minWidth: 'unset',
    fontSize: theme.typography.fontSize.small,
}));

const ExportSingleAgeEstimatesButton: FC<Props> = ({
    selectedConditionsDataTypes,
    selectedRefinementFilters,
    setModalVisibility,
    supportedDataTypes,
    disabled,
    selectedFilters,
    granularity,
    ...props
}) => {
    const { isCompact } = useDataExplorerLayout();

    const openExportSingleAgeEstimatesModal = useCallback(() => {
        setModalVisibility(true);
    }, [setModalVisibility]);

    const isSupportedDataTypeSelected = useMemo(
        () => !isEmpty(intersection(selectedConditionsDataTypes, supportedDataTypes)),
        [selectedConditionsDataTypes, supportedDataTypes]
    );

    const selectedMetrics: number[] = useMemo(
        () => selectedRefinementFilters[METRIC_ID_KEY] || [],
        [selectedRefinementFilters]
    ) as number[];

    const supportedMetrics: number[] = useMemo(() => {
        const granularityValues = (granularity?.[METRIC_ID_KEY] as number[]) ?? [];
        if (isEmpty(granularityValues)) {
            return selectedMetrics;
        }
        return granularityValues;
    }, [granularity, selectedMetrics]);

    const isSupportedMetricSelected = useMemo(
        () => isEmpty(selectedMetrics) || !isEmpty(intersection(selectedMetrics, supportedMetrics)),
        [selectedMetrics, supportedMetrics]
    );

    const isDisabled = disabled || !(isSupportedDataTypeSelected && isSupportedMetricSelected);

    const tooltipTitle = useMemo(() => {
        let title = ``;
        if (!isSupportedDataTypeSelected) {
            title += _('export_single_age_disabled_tooltip_label', {
                unsupported: selectedConditionsDataTypes
                    .map((type) => _(`data_type_${type}`))
                    .join(', '),
                supported: supportedDataTypes.map((dataType) => _(dataType)).join(', '),
                verb: pluralizeVerb(selectedConditionsDataTypes.length),
            });
        }
        if (!isSupportedMetricSelected && !isEmpty(selectedMetrics)) {
            title += isSupportedDataTypeSelected ? '' : '\n';
            title += _('export_single_age_disabled_tooltip_label', {
                unsupported: selectedMetrics.map((unit) => _(`metric_${unit}`)).join(', '),
                supported: supportedMetrics.map((id) => _(`metric_${id}`)).join(', '),
                verb: pluralizeVerb(selectedMetrics.length),
            });
        }
        if (
            isSupportedDataTypeSelected &&
            isSupportedMetricSelected &&
            disabled &&
            !isEmpty(granularity)
        ) {
            title += isSupportedDataTypeSelected && isSupportedMetricSelected ? '' : '\n';

            const [missingKey, missingValues] = Object.entries(selectedFilters).find(
                ([key, value]) => isEmpty(intersection(granularity?.[key], value as number[]))
            );

            if (missingKey && missingValues) {
                if (!isEmpty(granularity[missingKey])) {
                    title += _('export_single_age_disabled_tooltip_label', {
                        unsupported: formatValue(missingKey, missingValues as number[], _),
                        supported: formatValue(missingKey, granularity[missingKey] as number[], _),
                        verb: pluralizeVerb(missingValues.length),
                    });
                } else {
                    title += _('export_single_age_no_data_tooltip_label', {
                        filter: _(`refinement_${missingKey}`).toLowerCase(),
                        unsupported: formatValue(missingKey, missingValues as number[], _),
                    });
                }
            }
        }
        return title;
    }, [
        disabled,
        granularity,
        isSupportedDataTypeSelected,
        isSupportedMetricSelected,
        selectedConditionsDataTypes,
        selectedFilters,
        selectedRefinementFilters,
        supportedDataTypes,
    ]);

    const button = (
        <StyledButton
            onClick={openExportSingleAgeEstimatesModal}
            disabled={isDisabled}
            isTextButton
            beforeIconSrc="/icons/ic-file.svg"
            size="2xs"
            {...props}
        >
            {_('export_single_age_estimates_label')}
        </StyledButton>
    );

    return (
        <StyledRow {...props} isCompact={isCompact}>
            {isDisabled ? (
                <Tooltip placement="right" title={tooltipTitle}>
                    {button}
                </Tooltip>
            ) : (
                button
            )}
        </StyledRow>
    );
};

export default ExportSingleAgeEstimatesButton;
